/**
 * src/shell/admin/components/UserManagementConsole.tsx
 *
 * Admin User & Ambassador Management Dashboard.
 * Allows Superadmins & Mesh Owners to:
 * 1. View all registered mesh users & their credentials.
 * 2. Elect/Revoke Ambassador invite privileges with quotas.
 * 3. Inspect the live Single-Use Invite Code Ledger.
 * 4. Generate new invite codes with role and email restrictions.
 * 5. Suspend or reactivate user accounts.
 */

import React, { useState } from 'react';
import { inviteCodeRegistry } from '../../auth/services/InviteCodeRegistry';
import { MeshUserRecord, InviteCode } from '../../auth/types/invite-types';
import { UserRole } from '../../../main/security/RoleEnforcement';

export interface UserManagementConsoleProps {
  onClose?: () => void;
  adminUserId: string;
}

export const UserManagementConsole: React.FC<UserManagementConsoleProps> = ({
  onClose,
  adminUserId,
}) => {
  const [activeTab, setActiveTab] = useState<'USERS' | 'INVITES'>('USERS');
  const [users, setUsers] = useState<MeshUserRecord[]>(inviteCodeRegistry.getAllUsers());
  const [invites, setInvites] = useState<InviteCode[]>(inviteCodeRegistry.getAllInvites());
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteRole, setNewInviteRole] = useState<UserRole>('ordinary_user');
  const [feedbackMsg, setFeedbackMsg] = useState('');

  const refreshData = () => {
    setUsers(inviteCodeRegistry.getAllUsers());
    setInvites(inviteCodeRegistry.getAllInvites());
  };

  const handleToggleAmbassador = (user: MeshUserRecord) => {
    if (user.isAmbassador) {
      inviteCodeRegistry.revokeAmbassadorStatus(adminUserId, user.userId);
      setFeedbackMsg(`Revoked Ambassador rights from ${user.displayName}`);
    } else {
      inviteCodeRegistry.grantAmbassadorStatus(adminUserId, user.userId, 5);
      setFeedbackMsg(`Granted Ambassador status to ${user.displayName} (Quota: 5 invites)`);
    }
    refreshData();
    setTimeout(() => setFeedbackMsg(''), 3000);
  };

  const handleToggleSuspend = (user: MeshUserRecord) => {
    const nextStatus = user.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    inviteCodeRegistry.updateUserStatus(user.userId, nextStatus);
    setFeedbackMsg(`Updated ${user.displayName} status to ${nextStatus}`);
    refreshData();
    setTimeout(() => setFeedbackMsg(''), 3000);
  };

  const handleGenerateInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const res = inviteCodeRegistry.generateInvite(adminUserId, {
      boundEmail: newInviteEmail.trim() || undefined,
      boundRole: newInviteRole,
      expiresHours: 168,
    });

    if (res.success && res.invite) {
      setFeedbackMsg(`Generated single-use code: ${res.invite.code}`);
      setNewInviteEmail('');
      refreshData();
    } else {
      setFeedbackMsg(`Failed to generate invite: ${res.error}`);
    }
    setTimeout(() => setFeedbackMsg(''), 5000);
  };

  return (
    <div
      data-testid="user-management-console"
      style={{
        background: 'rgba(10, 15, 29, 0.98)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        padding: '24px',
        color: '#f8fafc',
        width: '100%',
        maxWidth: '1040px',
        margin: '0 auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.9)',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
            }}
          >
            👥
          </div>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>
              User Directory &amp; Ambassador Governance
            </h2>
            <p style={{ fontSize: '11px', color: '#94a3b8', margin: '2px 0 0 0' }}>
              Manage registered mesh principals, delegate invite rights, and monitor single-use quotas
            </p>
          </div>
        </div>

        {onClose && (
          <button
            type="button"
            data-testid="btn-user-mgmt-close"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              color: '#94a3b8',
              padding: '6px 12px',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Close ✕
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '10px' }}>
        <button
          type="button"
          data-testid="tab-users-directory"
          onClick={() => setActiveTab('USERS')}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            background: activeTab === 'USERS' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
            color: activeTab === 'USERS' ? '#60a5fa' : '#94a3b8',
            fontWeight: 700,
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          Registered Users ({users.length})
        </button>
        <button
          type="button"
          data-testid="tab-invites-ledger"
          onClick={() => setActiveTab('INVITES')}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            background: activeTab === 'INVITES' ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
            color: activeTab === 'INVITES' ? '#c084fc' : '#94a3b8',
            fontWeight: 700,
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          Invite Code Ledger ({invites.length})
        </button>
      </div>

      {/* Toast Feedback */}
      {feedbackMsg && (
        <div
          data-testid="mgmt-feedback-banner"
          style={{
            background: 'rgba(59, 130, 246, 0.15)',
            border: '1px solid rgba(59, 130, 246, 0.4)',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '12px',
            color: '#93c5fd',
            marginBottom: '16px',
          }}
        >
          ℹ️ {feedbackMsg}
        </div>
      )}

      {/* Content: Users Directory */}
      {activeTab === 'USERS' && (
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: '#94a3b8' }}>
                <th style={{ padding: '10px' }}>User</th>
                <th style={{ padding: '10px' }}>Role</th>
                <th style={{ padding: '10px' }}>Ambassador Status</th>
                <th style={{ padding: '10px' }}>Status</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.userId}
                  data-testid={`user-row-${u.userId}`}
                  style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}
                >
                  <td style={{ padding: '10px' }}>
                    <div style={{ fontWeight: 700, color: '#f8fafc' }}>{u.displayName}</div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>{u.email || u.userId}</div>
                  </td>
                  <td style={{ padding: '10px' }}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '6px',
                        fontSize: '10px',
                        fontWeight: 700,
                        background:
                          u.role === 'superadmin'
                            ? 'rgba(239, 68, 68, 0.2)'
                            : u.role === 'mesh_owner'
                            ? 'rgba(168, 85, 247, 0.2)'
                            : 'rgba(59, 130, 246, 0.2)',
                        color:
                          u.role === 'superadmin'
                            ? '#fca5a5'
                            : u.role === 'mesh_owner'
                            ? '#d8b4fe'
                            : '#93c5fd',
                      }}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: '10px' }}>
                    {u.isAmbassador ? (
                      <span style={{ color: '#34d399', fontWeight: 600 }}>
                        🎖️ Ambassador ({u.ambassadorQuota?.remainingCount ?? '∞'} left)
                      </span>
                    ) : (
                      <span style={{ color: '#64748b' }}>Standard User</span>
                    )}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <span
                      style={{
                        color: u.status === 'ACTIVE' ? '#10b981' : '#ef4444',
                        fontWeight: 600,
                      }}
                    >
                      ● {u.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                      {u.role !== 'superadmin' && (
                        <>
                          <button
                            type="button"
                            data-testid={`btn-toggle-ambassador-${u.userId}`}
                            onClick={() => handleToggleAmbassador(u)}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              border: '1px solid rgba(255, 255, 255, 0.15)',
                              background: u.isAmbassador ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                              color: u.isAmbassador ? '#fca5a5' : '#6ee7b7',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {u.isAmbassador ? 'Revoke Ambassador' : 'Elect Ambassador'}
                          </button>
                          <button
                            type="button"
                            data-testid={`btn-toggle-suspend-${u.userId}`}
                            onClick={() => handleToggleSuspend(u)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '6px',
                              border: '1px solid rgba(255, 255, 255, 0.15)',
                              background: 'rgba(255, 255, 255, 0.05)',
                              color: u.status === 'ACTIVE' ? '#fca5a5' : '#86efac',
                              fontSize: '11px',
                              cursor: 'pointer',
                            }}
                          >
                            {u.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Content: Invite Code Ledger & Generator */}
      {activeTab === 'INVITES' && (
        <div>
          {/* Quick Generator Form */}
          <form
            onSubmit={handleGenerateInvite}
            data-testid="form-generate-invite"
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '12px',
              padding: '14px',
              marginBottom: '20px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: 1, minWidth: '200px' }}>
              <input
                type="email"
                data-testid="input-invite-email"
                placeholder="Optional bound email (e.g. alice@mesh.local)"
                value={newInviteEmail}
                onChange={(e) => setNewInviteEmail(e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: '12px',
                }}
              />
            </div>
            <div>
              <select
                data-testid="select-invite-role"
                value={newInviteRole}
                onChange={(e) => setNewInviteRole(e.target.value as UserRole)}
                style={{
                  background: '#0f172a',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  color: '#fff',
                  fontSize: '12px',
                }}
              >
                <option value="ordinary_user">Role: Ordinary User</option>
                <option value="mesh_owner">Role: Mesh Owner</option>
              </select>
            </div>
            <button
              type="submit"
              data-testid="btn-submit-generate-invite"
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Generate Single-Use Code ⚡
            </button>
          </form>

          {/* Invites Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: '#94a3b8' }}>
                <th style={{ padding: '10px' }}>Code</th>
                <th style={{ padding: '10px' }}>Status</th>
                <th style={{ padding: '10px' }}>Creator</th>
                <th style={{ padding: '10px' }}>Bound Role / Email</th>
                <th style={{ padding: '10px' }}>Claimed By</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr
                  key={inv.code}
                  data-testid={`invite-row-${inv.code}`}
                  style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}
                >
                  <td style={{ padding: '10px', fontFamily: 'monospace', fontWeight: 700, color: '#93c5fd' }}>
                    {inv.code}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '6px',
                        fontSize: '10px',
                        fontWeight: 700,
                        background:
                          inv.status === 'UNUSED'
                            ? 'rgba(16, 185, 129, 0.2)'
                            : inv.status === 'CLAIMED'
                            ? 'rgba(148, 163, 184, 0.2)'
                            : 'rgba(239, 68, 68, 0.2)',
                        color:
                          inv.status === 'UNUSED'
                            ? '#6ee7b7'
                            : inv.status === 'CLAIMED'
                            ? '#94a3b8'
                            : '#fca5a5',
                      }}
                    >
                      {inv.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px' }}>
                    <div>{inv.createdBy}</div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>({inv.creatorRole})</div>
                  </td>
                  <td style={{ padding: '10px' }}>
                    <div>{inv.boundRole || 'ordinary_user'}</div>
                    {inv.boundEmail && <div style={{ fontSize: '10px', color: '#94a3b8' }}>{inv.boundEmail}</div>}
                  </td>
                  <td style={{ padding: '10px', color: inv.claimedBy ? '#f8fafc' : '#64748b' }}>
                    {inv.claimedBy ? `${inv.claimedBy} (${new Date(inv.claimedAt!).toLocaleTimeString()})` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
